/**
 * WhitebooksSessionCard — per-GSTIN auth surface.
 *
 * Renders one card showing the live Whitebooks session state for one
 * GST client (status pill + token + cycle expiries) plus three
 * action buttons: Authenticate (starts the OTP flow), Refresh now
 * (force a token refresh inside the cycle), Logout (clear the
 * session and force a re-OTP).
 *
 * The card SELF-GATES: when the org's configured GST fetch method
 * isn't `whitebooks_api`, the card renders nothing (returns null) so
 * mount sites don't need their own conditionals.
 *
 * Designed to be reused in:
 *   - GST Client detail page (Checkpoint 2 — main mount site).
 *   - Fetch-time inline retry modal (Checkpoint 2 — `compact` prop).
 *   - Trigger-time multi-client OTP popup (Checkpoint 3 — same
 *     component, multiple instances).
 *
 * Backend endpoints used:
 *   GET    /api/v1/gst/clients/{id}/whitebooks/status
 *   POST   /api/v1/gst/clients/{id}/whitebooks/start-otp
 *   POST   /api/v1/gst/clients/{id}/whitebooks/submit-otp
 *   POST   /api/v1/gst/clients/{id}/whitebooks/logout
 *
 * Props:
 *   clientId       (string, required)  GSTClient.id
 *   compact        (bool, default false) — collapsed layout for use
 *                  inside modals (no card chrome, no titles).
 *   onSessionReady (fn, optional)      — fired when the session
 *                  transitions to ready. Lets the fetch-time inline
 *                  retry modal auto-retry the original fetch.
 *   onClose        (fn, optional)      — used by the inline retry
 *                  modal to dismiss itself after a successful OTP.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Input,
  Modal,
  Popconfirm,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
  message as antMessage,
} from "antd";
import {
  ApiOutlined,
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  ExclamationCircleTwoTone,
  KeyOutlined,
  LogoutOutlined,
  MailOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import { fetchData, postData } from "../api/apiService";
import { LAMBDA } from "../api/endpointMap";

// Tenant Whitebooks endpoints in backend/app/api/v1/gst.py use the
// `_response()` helper, which wraps the payload as `{message, success,
// data}` (NOT `result` like the admin pages). The shared `unwrap()` from
// pages/admin/utils.js only knows about `result`, so it returned the
// entire raw object unchanged — `data.configured` was undefined and the
// card always rendered "Whitebooks not configured" even when the
// platform admin HAD configured Whitebooks. Local helper drains the
// right envelope.
const unwrapData = (raw) => {
  if (!raw || typeof raw !== "object") return raw;
  if ("data" in raw && raw.data && typeof raw.data === "object") return raw.data;
  if ("result" in raw && raw.result && typeof raw.result === "object") return raw.result;
  return raw;
};

const { Title, Text, Paragraph } = Typography;

// Short relative-time formatter — ISO -> "in 5h 27m" / "12 days ago".
function formatRelative(iso) {
  if (!iso) return "-";
  const target = new Date(iso).getTime();
  const now = Date.now();
  const deltaSec = Math.round((target - now) / 1000);
  const abs = Math.abs(deltaSec);
  const sign = deltaSec >= 0 ? "in " : "";
  const suffix = deltaSec >= 0 ? "" : " ago";
  if (abs < 60) return `${sign}${abs}s${suffix}`;
  if (abs < 3600) return `${sign}${Math.round(abs / 60)} min${suffix}`;
  if (abs < 86400) {
    const h = Math.floor(abs / 3600);
    const m = Math.round((abs % 3600) / 60);
    return `${sign}${h}h ${m}m${suffix}`;
  }
  return `${sign}${Math.round(abs / 86400)} days${suffix}`;
}

// Pick a colour + icon + label for the current session state.
function deriveBadge(status) {
  if (!status) return { color: "default", icon: null, label: "Loading…" };
  if (!status.configured) {
    return {
      color: "warning",
      icon: <ExclamationCircleTwoTone twoToneColor="#d97706" />,
      label: "GST API not configured",
    };
  }
  if (!status.identity_ready) {
    return {
      color: "warning",
      icon: <ExclamationCircleTwoTone twoToneColor="#d97706" />,
      label: "GST username / GSTIN missing",
    };
  }
  const s = status.session;
  if (!s || !s.has_auth_token) {
    return {
      color: "warning",
      icon: <ExclamationCircleTwoTone twoToneColor="#d97706" />,
      label: "OTP required",
    };
  }
  if (!s.cycle_alive) {
    return {
      color: "error",
      icon: <CloseCircleTwoTone twoToneColor="#dc2626" />,
      label: "30-day cycle expired - re-OTP",
    };
  }
  if (!s.token_alive) {
    return {
      color: "error",
      icon: <CloseCircleTwoTone twoToneColor="#dc2626" />,
      label: "Token expired",
    };
  }
  // Both alive — flag re-OTP within 3 days of cycle end.
  const cycleEnd = s.otp_cycle_expires_at
    ? new Date(s.otp_cycle_expires_at).getTime()
    : null;
  const daysToEnd = cycleEnd ? (cycleEnd - Date.now()) / 86400000 : null;
  if (daysToEnd !== null && daysToEnd <= 3) {
    return {
      color: "warning",
      icon: <ExclamationCircleTwoTone twoToneColor="#d97706" />,
      label: `Re-OTP due in ${Math.max(0, Math.ceil(daysToEnd))}d`,
    };
  }
  return {
    color: "success",
    icon: <CheckCircleTwoTone twoToneColor="#16a34a" />,
    label: "Ready",
  };
}

export default function WhitebooksSessionCard({
  clientId,
  compact = false,
  onSessionReady,
  onClose,
}) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);  // "start-otp" | "refresh" | "logout" | "submit"
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [otpValue, setOtpValue] = useState("");

  // `otpInFlight` is the FE-only memory of "an OTP was just triggered for
  // this client and the modal can be reopened without firing a new SMS".
  // Set true on a successful Start-OTP response; cleared on successful
  // Submit OTP, on Logout, or on remount (page reload). This means
  // closing the modal by accident does NOT cost an SMS — the user can
  // click "Re-open OTP entry" and paste the code they have. Holding it
  // only in component state (not localStorage) is on purpose: we don't
  // want a stale 30-min-old "in flight" hint to outlive the actual
  // Whitebooks `txn` validity window (~10 min).
  const [otpInFlight, setOtpInFlight] = useState(false);
  // Confirmation gate — Authenticate click opens THIS popup first.
  // The actual `/start-otp` wire call only fires after the user
  // clicks "Send OTP" here. Prevents accidental Authenticate clicks
  // from spamming the GST portal + landing the user in an already-
  // failed state with a fresh (unused, but soon-invalidated) SMS.
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  // Sticky error block — surfaces 4xx / 5xx detail from start-otp or
  // submit-otp so a fleeting antd toast doesn't bury the message.
  // Cleared when the user clicks Authenticate again (a fresh attempt).
  const [submitGateError, setSubmitGateError] = useState(null);
  // Timestamp (ms) of the most recent Authenticate click. Used to
  // render an informational "OTP triggered Xs ago" timer in the OTP
  // modal. The timer is INFORMATIONAL ONLY — it does not gate Submit.
  // The GST portal is the only authority on whether the OTP is still
  // valid; we just show the user how much time has passed so they can
  // judge whether to wait or trigger a fresh one.
  const [otpRequestedAt, setOtpRequestedAt] = useState(null);
  // Tick state — bumped every second while the modal is open so the
  // countdown text re-renders without us forcing a parent re-render.
  const [, setNowTick] = useState(0);

  // Used to fire onSessionReady exactly once per transition.
  const wasReady = useRef(false);

  const load = useCallback(async () => {
    if (!clientId) return null;
    setLoading(true);
    try {
      const raw = await fetchData(LAMBDA.GST_CLIENT_WHITEBOOKS_STATUS(clientId));
      const data = unwrapData(raw) || {};
      setStatus(data);
      // Fire onSessionReady on transition false -> true.
      const ready = Boolean(data?.session?.ready);
      if (ready && !wasReady.current) {
        wasReady.current = true;
        onSessionReady?.(data);
      } else if (!ready) {
        wasReady.current = false;
      }
      return data;
    } catch (e) {
      antMessage.error(
        e?.detail || e?.message || "Failed to load GST API session status",
      );
      return null;
    } finally {
      setLoading(false);
    }
  }, [clientId, onSessionReady]);

  useEffect(() => {
    load();
  }, [load]);

  // Tick the countdown timer every second while the OTP modal is open
  // and we have a known request timestamp. Stops automatically when
  // the modal closes — no leak.
  useEffect(() => {
    if (!otpModalOpen || !otpRequestedAt) return undefined;
    const id = setInterval(() => setNowTick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [otpModalOpen, otpRequestedAt]);

  // Step 1 — Authenticate click opens the confirmation dialog only.
  // No wire call yet. The user must explicitly confirm "Send OTP"
  // before we hit the GST portal (previously we were firing straight
  // through, which caused users to spam the button and invalidate
  // in-flight OTPs).
  const handleStartOtp = () => {
    setSubmitGateError(null);
    setConfirmModalOpen(true);
  };

  // Step 2 — user confirmed "Send OTP" in the modal. NOW we open the
  // OTP entry popup optimistically + fire the BE call in background.
  const confirmAndSendOtp = () => {
    setConfirmModalOpen(false);
    setOtpInFlight(true);
    setOtpModalOpen(true);
    setBusy("start-otp");
    setSubmitGateError(null);
    setOtpRequestedAt(Date.now());
    antMessage.info(
      "OTP request sent. The SMS lands on the GSTIN owner's registered mobile - paste it here when it arrives.",
    );
    postData(LAMBDA.GST_CLIENT_WHITEBOOKS_START_OTP(clientId), {})
      .then(() => {
        // Successful 202 — reload status so the badge transitions to
        // pending_otp_verification and the Re-open button gate flips on.
        return load();
      })
      .catch((e) => {
        // Hard fail from the BE (4xx / 5xx) — the OTP was never
        // triggered. Close the optimistic modal so the user isn't
        // typing into a void; surface the BE detail message so they
        // can fix the underlying problem (e.g. invalid gst_username).
        setOtpModalOpen(false);
        setOtpInFlight(false);
        // `postData` returns the parsed detail STRING on failure; handle
        // both shapes defensively.
        const msg =
          typeof e === "string"
            ? e
            : e?.detail ||
              e?.message ||
              "OTP request failed. Try Authenticate again.";
        antMessage.error({ content: msg, duration: 12 });
        setSubmitGateError(msg);
      })
      .finally(() => setBusy(null));
  };

  const handleSubmitOtp = () => {
    const otp = (otpValue || "").trim();
    if (!otp) {
      antMessage.error("Enter the OTP from the GSTIN owner's SMS.");
      return;
    }
    // The Whitebooks `/authentication/authtoken` exchange can take 30–
    // 120 seconds via their CloudFront edge. We DON'T await the request
    // in the click handler — that would freeze the modal with no escape
    // hatch. Instead we fire the request in the background, close the
    // modal immediately, and reload the session status when the BE
    // eventually responds (success → card flips to Ready; failure →
    // error toast + the per-GSTIN log row stamps the failure).
    setBusy("submit");
    setSubmitGateError(null);
    antMessage.info(
      "OTP submitted. Verifying with the GST portal - this can take up to a minute.",
    );
    // Close the modal now so the user isn't stranded watching a spinner.
    // `otpInFlight` stays true: if verification fails, "Re-open OTP entry"
    // will reappear so the user can retry with the same SMS.
    setOtpModalOpen(false);
    setOtpValue("");

    // BE returns 202 fast (the actual upstream call runs as a
    // FastAPI BackgroundTask). We then poll /status every 4 seconds
    // until the row state transitions out of pending_otp_verification,
    // and react to the eventual outcome there.
    postData(LAMBDA.GST_CLIENT_WHITEBOOKS_SUBMIT_OTP(clientId), { otp })
      .then(() => {
        // Poll /status every 4s for up to 90s. Exit early when the BE
        // row transitions to a terminal state (session ready OR
        // last_error stamped — see backend `_derive_state`).
        let elapsed = 0;
        const tick = async () => {
          elapsed += 4;
          const data = await load();
          const ready = Boolean(data?.session?.ready);
          const failed = Boolean(data?.session?.last_error);
          if (ready) {
            antMessage.success(
              "Session started. The background refresh will keep this GSTIN authenticated for 30 days.",
            );
            setOtpInFlight(false);
            setBusy(null);
            return;
          }
          if (failed) {
            antMessage.error(
              data?.session?.last_error ||
                "OTP submission failed. Click Authenticate for a fresh SMS.",
            );
            setBusy(null);
            return;
          }
          if (elapsed >= 90) {
            antMessage.warning(
              "Verification is taking longer than usual. Refresh the page in a minute, or check the GST Auth Sessions log.",
            );
            setBusy(null);
            return;
          }
          setTimeout(tick, 4000);
        };
        setTimeout(tick, 4000);
      })
      .catch((e) => {
        // `postData` calls `parseErrorMessage` which returns the
        // backend's detail string directly (NOT an object with .detail
        // / .message), so callers must handle the case where `e` is
        // already a string.
        const msg =
          typeof e === "string"
            ? e
            : e?.detail ||
              e?.message ||
              "OTP submission failed. Click Re-open OTP entry to try the code again, or Authenticate for a fresh one.";
        antMessage.error({ content: msg, duration: 12 });
        setBusy(null);
      });
  };

  const handleLogout = async () => {
    setBusy("logout");
    try {
      await postData(LAMBDA.GST_CLIENT_WHITEBOOKS_LOGOUT(clientId), {});
      antMessage.success("Logged out. Re-OTP required for the next fetch.");
      setOtpInFlight(false);
      await load();
    } catch (e) {
      antMessage.error(e?.detail || e?.message || "Logout failed.");
    } finally {
      setBusy(null);
    }
  };

  // Hooks must run unconditionally on every render — keep useMemo
  // ABOVE the self-gate early-return below, otherwise React's
  // rules-of-hooks tripwire fires the moment method_active flips.
  const badge = useMemo(() => deriveBadge(status), [status]);
  const session = status?.session;

  // SELF-GATE: when the method isn't whitebooks_api for this org,
  // render nothing so mount sites don't need their own conditional.
  if (!loading && status && status.method_active === false) {
    return null;
  }

  const Wrapper = compact ? "div" : Card;
  const wrapperProps = compact
    ? {}
    : {
        title: (
          <Space>
            <ApiOutlined />
            <span>GST API Session</span>
          </Space>
        ),
        size: "small",
        style: { marginTop: 16 },
      };

  return (
    <Wrapper {...wrapperProps}>
      {loading && !status ? (
        <Skeleton active paragraph={{ rows: 2 }} />
      ) : (
        <Space direction="vertical" size={10} style={{ width: "100%" }}>
          <Space align="center" wrap>
            {badge.icon}
            <Tag color={badge.color} style={{ fontWeight: 600 }}>
              {badge.label}
            </Tag>
            {/* In-flight indicator while the BE call to Whitebooks is
                still pending. The badge above reflects the LAST KNOWN
                state from the BE (e.g. "OTP required"); this chip
                signals that a fresh submit / authenticate is being
                verified upstream. */}
            {busy === "submit" && (
              <Tag color="processing" icon={<SyncOutlined spin />}>
                Verifying OTP with GST portal…
              </Tag>
            )}
            {busy === "start-otp" && (
              <Tag color="processing" icon={<SyncOutlined spin />}>
                Requesting OTP from GST portal…
              </Tag>
            )}
          </Space>

          {submitGateError && (
            <Alert
              type="error"
              showIcon
              closable
              onClose={() => setSubmitGateError(null)}
              message="OTP attempt blocked"
              description={
                <Text style={{ fontSize: 12 }}>{submitGateError}</Text>
              }
            />
          )}

          {!status?.configured && (
            <Alert
              type="warning"
              showIcon
              message="GST API not yet configured by the platform admin"
              description={
                <Text style={{ fontSize: 12 }}>
                  Notice fetches via the GST API cannot proceed until the
                  platform administrator finishes setup.
                </Text>
              }
            />
          )}

          {status?.configured && !status?.identity_ready && (
            <Alert
              type="warning"
              showIcon
              message="GST identity incomplete for this client"
              description={
                <Text style={{ fontSize: 12 }}>
                  This GST client is missing a valid <Text code>gst_username</Text>{" "}
                  or <Text code>gstin</Text>. Edit the client to add them, then
                  return here to authenticate.
                </Text>
              }
            />
          )}

          {status?.configured && status?.identity_ready && session && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <Text strong style={{ fontSize: 12 }}>
                  Token expires
                </Text>
                <div>
                  <Tooltip title={session.expires_at || "-"}>
                    <Text style={{ fontSize: 12 }}>
                      {formatRelative(session.expires_at)}
                    </Text>
                  </Tooltip>
                </div>
              </div>
              <div>
                <Text strong style={{ fontSize: 12 }}>
                  OTP cycle expires
                </Text>
                <div>
                  <Tooltip title={session.otp_cycle_expires_at || "-"}>
                    <Text style={{ fontSize: 12 }}>
                      {formatRelative(session.otp_cycle_expires_at)}
                    </Text>
                  </Tooltip>
                </div>
              </div>
              <div>
                <Text strong style={{ fontSize: 12 }}>
                  Refreshes
                </Text>
                <div>
                  <Text style={{ fontSize: 12 }}>
                    {session.refresh_count ?? 0}
                  </Text>
                </div>
              </div>
            </div>
          )}

          {session?.last_error && (
            <Alert
              type="error"
              showIcon
              message="Last error"
              description={
                <Text style={{ fontSize: 12 }}>
                  {session.last_error}
                  {session.last_error_at && (
                    <>
                      {" "}
                      <Text type="secondary">
                        ({formatRelative(session.last_error_at)})
                      </Text>
                    </>
                  )}
                </Text>
              }
            />
          )}

          <Space wrap>
            <Tooltip
              title={
                !status?.configured
                  ? "Platform admin must complete GST API setup first"
                  : !status?.identity_ready
                  ? "Add gst_username + gstin to this client first"
                  : session?.ready
                  ? "Logout first if you want to start a fresh OTP cycle"
                  : "Trigger an OTP to the GSTIN owner's registered mobile"
              }
            >
              <Button
                type="primary"
                icon={<MailOutlined />}
                onClick={handleStartOtp}
                loading={busy === "start-otp"}
                disabled={
                  !status?.configured ||
                  !status?.identity_ready ||
                  Boolean(session?.ready) ||
                  // Don't let the user fire a new OTP while we're still
                  // verifying the current one — the upstream call is
                  // slow and a parallel re-trigger would invalidate the
                  // in-flight code mid-verify.
                  busy === "submit"
                }
              >
                {session?.has_auth_token ? "Re-authenticate" : "Authenticate"}
              </Button>
            </Tooltip>
            {/* Re-open OTP entry — visible whenever the BE-reported
                state says an OTP has been requested but not yet
                successfully exchanged. The signal lives on the BE so
                it survives page reload, browser tab close, and
                multiple admins working on the same client — unlike
                the older `otpInFlight` flag which was per-component
                memory and disappeared on refresh. Re-opening does NOT
                fire a new SMS — it just brings back the OTP entry
                form so the user can paste the code that already
                arrived on the GSTIN owner's phone.
                Hidden while a submit is verifying (state would be
                pending too, but the modal can't be safely reopened
                with a partially-typed OTP mid-verify). */}
            {/* Re-open OTP entry is available when:
                - The session is pending (OTP fired, awaiting submit), OR
                - The last submit failed (user may want to try a different
                  OTP code from the same SMS / a prior SMS without
                  burning a new one).
                Hidden while a submit is verifying. */}
            {(status?.state === "pending_otp_verification" ||
              status?.state === "otp_verification_failed") &&
              !otpModalOpen &&
              busy !== "submit" && (
                <Tooltip title="Re-open the OTP entry form without sending a fresh SMS">
                  <Button
                    icon={<MailOutlined />}
                    onClick={() => setOtpModalOpen(true)}
                    disabled={busy === "logout"}
                  >
                    Re-open OTP entry
                  </Button>
                </Tooltip>
              )}
            {/* Reload button intentionally removed. Token refresh is
                handled by a background scheduler (cron) on a fixed
                cadence — exposing a manual reload here invites users
                to spam the button, which would (a) confuse them into
                thinking it triggers a fresh OTP, and (b) push needless
                load against the GST API in aggregate. Status updates
                land on the next natural page interaction (Submit OTP /
                Logout / page reload). */}
            <Popconfirm
              title="Logout this client's GST API session?"
              description="Token will be cleared. You will need to enter a fresh OTP before the next fetch can use this GSTIN."
              okText="Logout"
              okType="danger"
              onConfirm={handleLogout}
              disabled={!session?.has_auth_token}
            >
              <Button
                icon={<LogoutOutlined />}
                danger
                loading={busy === "logout"}
                disabled={!session?.has_auth_token}
              >
                Logout
              </Button>
            </Popconfirm>
            {onClose && (
              <Button onClick={onClose} disabled={Boolean(busy)}>
                Close
              </Button>
            )}
          </Space>

          {!compact && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              The OTP is sent by the GST portal directly to the GSTIN
              owner's registered mobile number. The platform does not
              receive it - collect it from the client, then submit
              below. After a successful OTP, the background refresh
              cycle keeps this GSTIN authenticated for 30 days.
            </Text>
          )}
        </Space>
      )}

      {/* Confirmation dialog for Authenticate — appears BEFORE we
          fire /start-otp against the GST portal. Prevents accidental
          Authenticate clicks from wasting an SMS + causing repeated
          Invalid transaction errors. */}
      <Modal
        open={confirmModalOpen}
        centered
        title={
          <Space>
            <MailOutlined />
            <span>Send OTP to the GSTIN owner?</span>
          </Space>
        }
        onCancel={() => setConfirmModalOpen(false)}
        onOk={confirmAndSendOtp}
        okText="Send OTP"
        cancelText="Cancel"
        maskClosable={false}
      >
        <Paragraph style={{ marginBottom: 8 }}>
          The GST portal will send an OTP by SMS to the mobile number
          registered against{" "}
          <Text code>{status?.gstin || "this GSTIN"}</Text>'s owner.
        </Paragraph>
        <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 12 }}>
          Only click <Text strong>Send OTP</Text> when the client is
          ready to share the code with you. Sending another OTP later
          will invalidate this one.
        </Paragraph>
      </Modal>

      <Modal
        open={otpModalOpen}
        title={
          <Space>
            <KeyOutlined />
            <span>Submit OTP from the GSTIN owner</span>
          </Space>
        }
        onCancel={() => {
          // Closing the dialog does NOT cancel the in-flight start-otp
          // call — the SMS will still arrive on the GSTIN owner's
          // phone. The user can come back via "Re-open OTP entry" on
          // the card to paste the code without burning a new SMS.
          setOtpModalOpen(false);
          setOtpValue("");
        }}
        onOk={handleSubmitOtp}
        okText="Submit OTP"
        cancelText="Close (re-open later)"
        okButtonProps={{ loading: busy === "submit", disabled: !otpValue.trim() }}
        maskClosable={false}
        keyboard={false}
      >
        <Space direction="vertical" size={10} style={{ width: "100%" }}>
          <Paragraph style={{ marginBottom: 0, fontSize: 13 }}>
            The GST portal has sent an OTP to the registered mobile of{" "}
            <Text code>{status?.gstin || "this GSTIN"}</Text>'s owner. Collect
            the 6-digit code from them and paste it below.
          </Paragraph>
          <Input
            value={otpValue}
            onChange={(e) => setOtpValue(e.target.value)}
            placeholder="123456"
            autoFocus
            onPressEnter={handleSubmitOtp}
            maxLength={20}
          />
          {otpRequestedAt && (() => {
            // Informational timer only — the GST portal is the sole
            // authority on whether the OTP is still valid. We never
            // disable Submit based on this number.
            const elapsedSec = Math.max(
              0,
              Math.floor((Date.now() - otpRequestedAt) / 1000),
            );
            const mm = Math.floor(elapsedSec / 60);
            const ss = elapsedSec % 60;
            const label =
              mm > 0 ? `${mm}m ${String(ss).padStart(2, "0")}s` : `${ss}s`;
            return (
              <Tag color={elapsedSec < 600 ? "blue" : "default"}>
                OTP triggered {label} ago
              </Tag>
            );
          })()}
          <Text type="secondary" style={{ fontSize: 11 }}>
            Verification with the GST portal can take up to a minute -
            after you click Submit, this dialog will close and the
            session card will update once verification completes.
            Closing this dialog by accident is safe - use "Re-open OTP
            entry" to come back without requesting a new SMS.
          </Text>
        </Space>
      </Modal>
    </Wrapper>
  );
}
